import { BUTTON, type InputBits } from "../sim";

/**
 * Нажатия по времени для постановочных сцен и боя человеком по записи (GAME-31): [с кадра, по кадр, кнопки].
 * Кадры — от начала боя (после заставки раунда). В записи «вправо» — это «вперёд», к сопернику, «влево» — «назад»;
 * в кнопки экрана их переводит facedBits по тому, куда боец сейчас смотрит.
 */
export type Track = readonly (readonly [number, number, InputBits])[];

export const KEY = {
  F: BUTTON.right,
  B: BUTTON.left,
  D: BUTTON.down,
  U: BUTTON.up,
  P: BUTTON.lightPunch, // □
  HP: BUTTON.heavyPunch, // △
  K: BUTTON.lightKick, // ✕
  HK: BUTTON.heavyKick, // ○
  BL: BUTTON.block, // R2
  TA: BUTTON.taunt, // тачпад, T
  GR: BUTTON.grab, // L1, H
} as const;

/** Короткое нажатие кнопки: 3 кадра, как живой палец. */
export const tap = (frame: number, bits: InputBits): readonly [number, number, InputBits] => [frame, frame + 3, bits];

/** Человек жмёт кнопки подряд: buttons по очереди, раз в period кадров, держит hold кадров. */
export const mash = (frame: number, buttons: readonly InputBits[], period = 7, hold = 4): Track =>
  buttons.map((bits, index) => [frame + index * period, frame + index * period + hold, bits] as const);

/** Жест направлениями касаниями: каждое — 3 кадра, между ними 2 кадра пусто (как пальцем). */
export const gesture = (frame: number, directions: readonly InputBits[]): Track =>
  directions.map((bits, index) => [frame + index * 5, frame + index * 5 + 3, bits] as const);

/** Бег: «вперёд», отпустить, снова «вперёд» и держать до кадра to. */
export const run = (frame: number, to: number): Track => [
  [frame, frame + 5, KEY.F],
  [frame + 8, to, KEY.F],
];

/** Кнопки игрока в кадре: объединение всех отрезков, которые его накрывают. */
export function bitsAt(track: Track, frame: number): InputBits {
  let bits = 0;
  for (const [from, to, value] of track) if (frame >= from && frame < to) bits |= value;
  return bits;
}

/** Кнопки записи в кадре для бойца, который смотрит в facing: лицом влево «вперёд» и «назад» меняются местами. */
export function facedBits(track: Track, frame: number, facing: number): InputBits {
  const value = bitsAt(track, frame);
  if (facing >= 0) return value;
  const right = (value & BUTTON.right) !== 0;
  const left = (value & BUTTON.left) !== 0;
  const rest = value & ~(BUTTON.right | BUTTON.left);
  return rest | (right ? BUTTON.left : 0) | (left ? BUTTON.right : 0);
}
